/** Author: Claude Code // Generation: Authentication Store for user management with redirect support // Version: v3.2.226 // Modified: 2026-04-02T18:36:00-07:00 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { resolveSocketTarget } from '../services/websocket/socketUrl';

// LocalStorage keys
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

interface User {
  id: string;
  username: string;
  email?: string;
  virtualBalance: number;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  error: string | null;
  isLoading: boolean;

  // Modal control (v3.2.41)
  showAuthModal: boolean;
  authMode: 'signin' | 'signup';

  // v3.2.57: Redirect after authentication
  redirectAfterAuth: string | null;
}

interface AuthActions {
  login: (username: string, password: string) => Promise<any>;
  register: (userData: { username: string; email: string; password: string }) => Promise<any>;
  loginWithGoogle: (idToken: string) => Promise<any>;
  logout: () => void;
  setError: (error: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;

  // Modal actions (v3.2.41)
  openAuthModal: (mode: 'signin' | 'signup', redirectTo?: string) => void;
  closeAuthModal: () => void;
  toggleAuthModal: (show: boolean) => void;
  setAuthMode: (mode: 'signin' | 'signup') => void;

  // v3.2.57: Redirect management
  setRedirectAfterAuth: (path: string | null) => void;
  getAndClearRedirect: () => string | null;
}

// Helper functions for localStorage
const loadAuthFromStorage = (): { token: string | null; user: User | null; isAuthenticated: boolean } => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    if (token && userStr) {
      const user = JSON.parse(userStr);
      return { token, user, isAuthenticated: true };
    }
  } catch (error) {
    console.error('Failed to load auth from localStorage:', error);
  }
  return { token: null, user: null, isAuthenticated: false };
};

const saveAuthToStorage = (token: string, user: User) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.error('Failed to save auth to localStorage:', error);
  }
};

const clearAuthFromStorage = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error('Failed to clear auth from localStorage:', error);
  }
};

export const useAuthStore = create<AuthState & AuthActions>()(
  immer((set, get) => {
    // Load initial state from localStorage
    const { token, user, isAuthenticated } = loadAuthFromStorage();

    return {
      // Initial state
      isAuthenticated,
      user,
      token,
      error: null,
      isLoading: false,
      showAuthModal: false,
      authMode: 'signin',
      redirectAfterAuth: null,

    // Actions
    login: async (username: string, password: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        // Use persistent WebSocket connection for authentication
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;

        // Authenticate through WebSocket and get response with token
        const response = await webSocketService.authenticate(username, password);

        set((state) => {
          state.isLoading = false;
          if (response.success) {
            state.isAuthenticated = true;
            state.user = response.user;
            state.token = response.token; // ✅ 使用后端返回的真实token
            state.error = null;

            // Save to localStorage for persistence
            saveAuthToStorage(response.token, response.user);
          } else {
            // Handle error object or string
            state.error = typeof response.error === 'object' && response.error?.message
              ? response.error.message
              : (response.error || 'Login failed');
          }
        });

        return response;
      } catch (error) {
        set((state) => {
          state.isLoading = false;
          state.error = error instanceof Error ? error.message : 'Login failed';
        });

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Login failed'
        };
      }
    },

    // Google 一键登录
    loginWithGoogle: async (idToken: string) => {
      set((state) => { state.isLoading = true; state.error = null; });
      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const response = await webSocketService.authenticateWithGoogle(idToken);

        set((state) => {
          state.isLoading = false;
          if (response.success) {
            state.isAuthenticated = true;
            state.user = response.user;
            state.token = response.token;
            state.error = null;
            saveAuthToStorage(response.token, response.user);
          } else {
            state.error = typeof response.error === 'object' && response.error?.message
              ? response.error.message
              : (response.error || 'Google authentication failed');
          }
        });

        return response;
      } catch (error) {
        set((state) => { state.isLoading = false; state.error = error instanceof Error ? error.message : 'Google auth failed'; });
        return { success: false, error: error instanceof Error ? error.message : 'Google auth failed' };
      }
    },

    register: async (userData: { username: string; email: string; password: string }) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        // Import Socket.IO client directly for authentication
        const { io } = await import('socket.io-client');
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        const targetUrl = webSocketService.getConfig().url || 'http://localhost:8080';
        
        // Create direct socket connection for authentication using same URL as WebSocketService
        const socketTarget = resolveSocketTarget(targetUrl);
        const socket = io(socketTarget.url, {
          transports: ['polling'],
          ...(socketTarget.path ? { path: socketTarget.path } : {})
        });
        
        // Wait for connection
        await new Promise<void>((resolve, reject) => {
          socket.on('connect', () => {
            console.log('🔌 Auth WebSocket connected for registration');
            resolve();
          });
          socket.on('connect_error', (error) => {
            console.error('❌ Auth WebSocket connection failed:', error);
            reject(error);
          });
        });

        // Send register request via WebSocket with callback
        const data = await new Promise((resolve, reject) => {
          socket.emit('auth:register', userData, (response: any) => {
            socket.disconnect(); // Close connection after auth
            resolve(response);
          });
          
          // Timeout after 5 seconds
          setTimeout(() => {
            socket.disconnect();
            reject(new Error('Registration timeout'));
          }, 5000);
        });

        set((state) => {
          state.isLoading = false;
          if (data.success) {
            state.isAuthenticated = true;
            state.user = data.user;
            state.token = data.token;
            state.error = null;

            // Save to localStorage for persistence
            saveAuthToStorage(data.token, data.user);
          } else {
            // Handle error object or string
            state.error = typeof data.error === 'object' && data.error?.message
              ? data.error.message
              : (data.error || 'Registration failed');
          }
        });

        return data;
      } catch (error) {
        set((state) => {
          state.isLoading = false;
          state.error = error instanceof Error ? error.message : 'Registration failed';
        });

        return {
          success: false,
          error: error instanceof Error ? error.message : 'Registration failed'
        };
      }
    },

    logout: async () => {
      // Logout from WebSocket service
      try {
        const webSocketService = (await import('../services/websocket/WebSocketService')).default;
        webSocketService.logout();
      } catch (error) {
        console.error('Error during logout:', error);
      }

      // Clear localStorage
      clearAuthFromStorage();

      set((state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      });
    },

    setError: (error: string | null) => {
      set((state) => {
        state.error = error;
      });
    },

    setLoading: (isLoading: boolean) => {
      set((state) => {
        state.isLoading = isLoading;
      });
    },

    setUser: (user: User | null) => {
      set((state) => {
        state.user = user;
        state.isAuthenticated = !!user;
      });
    },

    setToken: (token: string | null) => {
      set((state) => {
        state.token = token;
      });
    },

    // Modal actions
    openAuthModal: (mode: 'signin' | 'signup', redirectTo?: string) => {
      set((state) => {
        state.showAuthModal = true;
        state.authMode = mode;
        if (redirectTo) {
          state.redirectAfterAuth = redirectTo;
        }
      });
    },

    closeAuthModal: () => {
      set((state) => {
        state.showAuthModal = false;
        state.error = null;
      });
    },

    toggleAuthModal: (show: boolean) => {
      set((state) => {
        state.showAuthModal = show;
        if (!show) {
          state.error = null;
        }
      });
    },

    setAuthMode: (mode: 'signin' | 'signup') => {
      set((state) => {
        state.authMode = mode;
        state.error = null;
      });
    },

    // v3.2.57: Redirect management
    setRedirectAfterAuth: (path: string | null) => {
      set((state) => {
        state.redirectAfterAuth = path;
      });
    },

    getAndClearRedirect: () => {
      const redirect = get().redirectAfterAuth;
      set((state) => {
        state.redirectAfterAuth = null;
      });
      return redirect;
    }
  }
  })
);
